import { useEffect, useRef, useState } from 'react';
import { Globe, ChevronDown, Check } from 'lucide-react';
import { getStoredLanguage, normalizeLanguage, setAppLanguage, SUPPORTED_LANGUAGES, DEFAULT_LANGUAGE } from '../utils/language';

const LANGUAGE_LABELS = {
  TR: 'Türkçe',
  EN: 'English',
  RU: 'Русский'
};

const LanguageSelector = ({
  className = '',
  size = 'md',
  onChange,
  labelMode = 'full',
  showIcon = true
}) => {
  const [language, setLanguage] = useState(DEFAULT_LANGUAGE);
  const [open, setOpen] = useState(false);
  const containerRef = useRef(null);

  useEffect(() => {
    setLanguage(getStoredLanguage());
    setOpen(false);
    const handler = (e) => {
      const next = normalizeLanguage(e?.detail?.language);
      setLanguage(next);
    };
    if (typeof window !== 'undefined') {
      window.addEventListener('app-language-change', handler);
    }
    return () => {
      if (typeof window !== 'undefined') {
        window.removeEventListener('app-language-change', handler);
      }
    };
  }, []);

  // close on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    if (open && typeof document !== 'undefined') {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      if (typeof document !== 'undefined') {
        document.removeEventListener('mousedown', handleClickOutside);
      }
    };
  }, [open]);

  const handleChange = (value) => {
    const normalized = setAppLanguage(value);
    setLanguage(normalized);
    if (onChange) onChange(normalized);
    setOpen(false);
  };

  const paddingClasses = size === 'sm' ? 'px-2 py-1 text-xs' : 'px-3 py-2 text-sm';
  const iconSize = size === 'sm' ? 'w-4 h-4' : 'w-5 h-5';
  const textSize = size === 'sm' ? 'text-xs' : 'text-sm';
  const displayLabel = labelMode === 'short' ? language : (LANGUAGE_LABELS[language] || language);

  return (
    <div ref={containerRef} className={`relative inline-flex ${className}`}>
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className={`inline-flex items-center bg-white border border-gray-200 rounded-full shadow-sm hover:shadow-md transition-shadow ${paddingClasses} ${textSize} font-medium text-gray-700`}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        {showIcon && <Globe className={`${iconSize} text-gray-600`} />}
        <span className={showIcon ? 'ml-2' : ''}>{displayLabel}</span>
        <ChevronDown className={`${iconSize} ml-2 text-gray-500`} />
      </button>

      {open && (
        <div
          className="absolute z-50 mt-2 right-0 top-full min-w-[160px] rounded-xl border border-gray-200 bg-white shadow-xl overflow-hidden"
          style={{ minWidth: '160px', maxWidth: '220px' }}
        >
          <ul role="listbox" className="py-1">
            {SUPPORTED_LANGUAGES.map((lang) => {
              const selected = lang === language;
              return (
                <li key={lang}>
                  <button
                    type="button"
                    role="option"
                    aria-selected={selected}
                    onClick={() => handleChange(lang)}
                    className={`w-full flex items-center justify-between px-3 py-2 text-left transition-colors ${
                      selected ? 'bg-primary-50 text-primary-700 font-semibold' : 'text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    <span>{LANGUAGE_LABELS[lang] || lang}</span>
                    {selected && <Check className="w-4 h-4 text-primary-600" />}
                  </button>
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </div>
  );
};

export default LanguageSelector;

